import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang, scrollToId } from "../App";
import { IMAGES } from "../images";
import { EASE } from "./Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="-mb-[0.16em] block overflow-hidden pb-[0.16em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();

  return (
    <section id="accueil" data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink">
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt={t.hero.alt}
          className="animate-kenburns h-full w-full object-cover"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/30 to-ink/70" />
      </div>

      <div className="relative z-10 flex h-full flex-col justify-end px-6 pb-24 md:px-12 md:pb-20 lg:px-20">
        <h1 className="font-serif text-[13vw] font-medium leading-[1.04] text-ivory sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLine delay={0.45}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.6}>
            <em className="text-champagne/90">{t.hero.line2}</em>
          </MaskedLine>
        </h1>
        <motion.p
          data-testid="hero-subline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.15 }}
          className="mt-7 text-[11px] font-medium uppercase tracking-[0.4em] text-ivory/60 md:text-xs"
        >
          {t.hero.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.35, ease: EASE }}
        >
          <button
            data-testid="hero-cta"
            onClick={() => scrollToId("contact")}
            className="group mt-10 flex items-center gap-4 border border-ivory/40 px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:border-bronze hover:bg-bronze md:text-xs"
          >
            {t.hero.cta}
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.8 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 sm:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/50" data-testid="hero-scroll-hint">
          {t.hero.scroll}
        </span>
        <span className="animate-scrollhint block h-10 w-px bg-ivory/60" />
      </motion.div>
    </section>
  );
}
